import { PlayerStatus } from '@tunein/web-player-manager';
import classNames from 'clsx';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { player } from 'src/client/player';
import {
  closeNowPlayingDialog,
  closeNowPlayingDialogAndTrack,
  fetchNowPlayingItems,
} from 'src/common/actions/dialog';
import ChevronRight from 'src/common/components/shared/svgIcons/ChevronRight';
import useActions from 'src/common/hooks/useActions';
import useExperiment from 'src/common/hooks/useExperiment';
import useOnMount from 'src/common/hooks/useOnMount';
import usePrevious from 'src/common/hooks/usePrevious';
import { useStores } from 'src/common/providers';
import { LocationAndLocalizationContext } from 'src/common/providers/LocationAndLocalizationProvider';
import { selectIsDiscord, selectIsMobile } from 'src/common/selectors/app';
import { selectIsVideoAdDialogOpen, selectNowPlayingGuideItem } from 'src/common/selectors/dialog';
import { selectIsExpiredWebSubscriber, selectIsUserSubscribed } from 'src/common/selectors/me';
import { ENABLE_PLAYBACK_SPEED_CONTROL } from '../../../constants/experiments/player';
import {
  ERROR_MESSAGE,
  LIVE,
  MEDIA_AD_SUBTITLE,
  MEDIA_AD_TITLE,
} from '../../../constants/localizations/player';
import connectWithAuth from '../../../decorators/auth/connectWithAuth';
import { selectIsDesktopNowPlayingEnabled } from '../../../selectors/config';
import cssVars from '../../../styles/variables';
import { is360 } from '../../../utils/breakpoints';
import createSubscribeLink from '../../../utils/subscription/createSubscribeLink';
import NowPlayingDialogAd from '../../ads/NowPlayingDialogAd';
import SpeedControl from '../../player/actionDrawer/SpeedControl';
import PlayButton from '../../player/controls/playButton/PlayButton';
import Scrubber from '../../player/controls/scrubber/Scrubber';
import VolumeControl from '../../player/controls/volume/VolumeControl';
import ClampText from '../../shared/ClampText';
import { FavoriteControl } from '../../shared/controls/favorite/FavoriteControl';
import ImageWithDefault from '../../shared/ImageWithDefault';
import FastForwardFifteenIcon from '../../shared/svgIcons/FastForwardFifteenIcon';
import LegacyCaretDown from '../../shared/svgIcons/LegacyCaretDown';
import RewindFifteenIcon from '../../shared/svgIcons/RewindFifteenIcon';
import UpsellRibbon from '../../subscription/UpsellRibbon';
import getNowPlayingContainerItemGuideId from '../utils/getNowPlayingContainerItemGuideId';
import css from './now-playing.module.scss';

const SOURCE = 'nowPlaying';
const idPrefix = 'nowPlayingDialog';

const getTitle = (nowPlaying, getLocalizedText) =>
  nowPlaying.rejectReasonKey
    ? getLocalizedText(nowPlaying.rejectReasonKey)
    : nowPlaying?.title || nowPlaying?.header?.title;
const getSubtitle = (nowPlaying) => nowPlaying?.subtitle || nowPlaying?.header?.subtitle;

// exported for testing
export function NowPlaying({ matchUrl, breakpoint, hideDisplayAd, selectedContentId }) {
  const actions = useActions({
    closeNowPlayingDialogAndTrack,
    closeNowPlayingDialog,
    fetchNowPlayingItems,
  });
  const { getLocalizedText } = useContext(LocationAndLocalizationContext);
  const isPlaybackRateExperimentEnabled = useExperiment(ENABLE_PLAYBACK_SPEED_CONTROL);
  const subtitleOuterContainerRef = useRef(null);
  const subtitleInnerContainerRef = useRef(null);
  const titleOuterContainerRef = useRef(null);
  const titleInnerContainerRef = useRef(null);
  const [isTitleScrolling, setIsTitleScrolling] = useState(false);
  const [isSubtitleScrolling, setIsSubtitleScrolling] = useState(false);
  const isDiscord = useSelector(selectIsDiscord);
  const isUserSubscribed = useSelector(selectIsUserSubscribed);
  const isExpiredWebSubscriber = useSelector(selectIsExpiredWebSubscriber);
  const isDesktopNowPlayingEnabled = useSelector(selectIsDesktopNowPlayingEnabled);
  const isVideoAdDialogOpen = useSelector(selectIsVideoAdDialogOpen);
  const isMobile = useSelector(selectIsMobile);
  const { playerStore, appStore } = useStores();
  const metadata = playerStore.metadata() || {};
  const upsell = metadata.upsell;
  const canShowUpsell = !!(!isUserSubscribed && upsell?.canUpsell && !isDiscord);
  const isLive = metadata.isLive;
  const canScrub = !isLive || metadata.isLiveSeek;
  const primaryGuideId = metadata.primaryGuideId;
  const guideItemPathname =
    appStore.guideItemPathname() || (primaryGuideId ? `/radio/${primaryGuideId}/` : '');
  const playerStatus = metadata.playerStatus;
  const isMediaAdLoaded = metadata.isAdBreak;
  const canChangePlaybackRate = metadata.canControlPlaybackRate;
  const streamRejectReason = metadata.rejectReason;
  const previousMetadata = usePrevious(metadata);
  const nowPlayingGuideItem = useSelector(selectNowPlayingGuideItem);
  const currentGuideItem = playerStore.currentGuideItem();
  const tunedGuideId = currentGuideItem?.playableGuideId;

  const startScrolling = () => {
    const titleInnerWidth = get(titleInnerContainerRef.current, 'current.scrollWidth');
    const titleOuterWidth = get(titleOuterContainerRef.current, 'current.clientWidth');
    const subtitleInnerWidth = get(subtitleInnerContainerRef.current, 'current.scrollWidth');
    const subtitleOuterWidth = get(subtitleOuterContainerRef.current, 'current.clientWidth');

    setIsTitleScrolling(titleInnerWidth > titleOuterWidth);
    setIsSubtitleScrolling(subtitleInnerWidth > subtitleOuterWidth);
  };

  useOnMount(() => {
    // NOTE: We only want to fetch new nowPlayingItems in the NowPlaying child component under specific circumstances
    // i.e. With the NowPlayingDialog open, the user clicks to tune a new station listed in one of the avail containers
    // If the currentlyPlayingGuideId differs from the guideId in the NowPlaying containerItem, only then refresh data
    if (
      !isEmpty(nowPlayingGuideItem.containerItems) &&
      !nowPlayingGuideItem.isFetching &&
      tunedGuideId &&
      tunedGuideId !== getNowPlayingContainerItemGuideId(nowPlayingGuideItem.containerItems)
    ) {
      actions.fetchNowPlayingItems(tunedGuideId);
    }

    const timeoutId = setTimeout(startScrolling, 2000);

    return () => {
      clearTimeout(timeoutId);
    };
  });

  useEffect(() => {
    const previousSubtitle = getSubtitle(previousMetadata);
    const newSubtitle = getSubtitle(metadata);
    const didPlayerTransitionToFailedState = !previousMetadata.error;

    if (previousSubtitle !== newSubtitle || didPlayerTransitionToFailedState) {
      // using setTimeout, as the refs aren't necessarily updating immediately
      setTimeout(startScrolling, 0);
    }
  }, [metadata, previousMetadata]);

  const processTitle = () => {
    const titleTextCss = isTitleScrolling ? css.scrolling : '';
    // NOTE: Render subtitle (e.g. Radiohead - Fake Plastic Trees) below image
    // TODO: Fix naming of these title and subtitle functions to properly reflect their usage
    const title = getSubtitle(metadata);
    const titleText = isMediaAdLoaded ? getLocalizedText(MEDIA_AD_TITLE) : title;
    const testId = isMediaAdLoaded ? 'MediaAdTitle' : 'Title';

    return {
      testId,
      titleText,
      titleTextCss,
    };
  };

  const processSubtitle = () => {
    // NOTE: Render title (e.g. ALT 92.9) in page header
    // TODO: Fix naming of these title and subtitle functions to properly reflect their usage
    const subtitle =
      playerStatus === PlayerStatus.FAILED
        ? getLocalizedText(ERROR_MESSAGE)
        : getTitle(metadata, getLocalizedText);
    const subtitleTextCss = isSubtitleScrolling ? css.scrolling : '';

    const subtitleText = isMediaAdLoaded ? getLocalizedText(MEDIA_AD_SUBTITLE) : subtitle;
    const testId = isMediaAdLoaded ? 'MediaAdSubtitle' : 'Subtitle';

    return {
      testId,
      subtitleText,
      subtitleTextCss,
    };
  };

  const renderDesktopNowPlaying = () => {
    return (
      <>
        <div className={css.topSection}>
          <div className={css.actionsAndTitle} data-testid="actionsAndTitle">
            <div
              onClick={actions.closeNowPlayingDialogAndTrack}
              data-testid={`${idPrefix}MinimizeContainer`}
              className={css.minimizeContainer}
            >
              <LegacyCaretDown
                key={`${idPrefix}Minimize`}
                className={css.minimizeButton}
                fill="white"
              />
            </div>
          </div>
        </div>
        <div className={css.innerDesktopContent}>
          <div className={css.flexContainer}>
            <ImageWithDefault
              id={`${idPrefix}Artwork`}
              containerClassName={css.artworkImageContainer}
              className={css.artworkImage}
              src={metadata.image}
              alt={metadata.title}
            />
            <div className={css.underImageTextContainer}>
              {renderDesktopTitle()}
              {renderDesktopSubtitle()}
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderDefaultNowPlaying = () => {
    const playButtonSize = is360(breakpoint) ? 80 : 60;
    const isDiscordMobile = isDiscord && isMobile;
    const shouldShowNewControls = isDiscordMobile && !streamRejectReason;
    const shouldShowSpeedControl =
      isPlaybackRateExperimentEnabled && canChangePlaybackRate && !isDiscordMobile;

    return (
      <>
        <div className={css.topSection}>
          <div
            className={classNames(css.actionsAndTitle, {
              [css.spaceBelowTitle]: !canShowUpsell,
              [css.shouldExpandSection]: isDiscord && !isMobile, // For the purpose of centering NP Image Content
            })}
            data-testid="actionsAndTitle"
          >
            <div
              onClick={actions.closeNowPlayingDialogAndTrack}
              data-testid={`${idPrefix}MinimizeContainer`}
              className={css.minimizeContainer}
            >
              <LegacyCaretDown
                key={`${idPrefix}Minimize`}
                className={css.minimizeButton}
                fill="white"
              />
            </div>
            {(!isDiscord || isMobile) && renderDefaultTitle()}
            {isMobile && (
              <div className={css.actionsContainer}>
                <VolumeControl
                  iconContainerClass={css.actionDrawerIconContainer}
                  volumeContainerClass={css.volumeContainer}
                  isDisabled={isVideoAdDialogOpen}
                  fill="#ffffff"
                />
                <FavoriteControl
                  fill="#ffffff"
                  iconClassName={css.heartIcon}
                  unFavoritedClassName={css.unfavorited}
                />
              </div>
            )}
          </div>
          {canShowUpsell && (
            <UpsellRibbon
              onButtonClick={actions.closeNowPlayingDialog}
              buttonUrl={createSubscribeLink({
                guideId: selectedContentId,
                source: SOURCE,
                location,
              })}
              className={css.upsellRibbon}
              upsell={upsell}
              isExpiredWebSubscriber={isExpiredWebSubscriber}
            />
          )}
        </div>
        {!isUserSubscribed && !hideDisplayAd && (
          <NowPlayingDialogAd matchUrl={matchUrl} guideId={selectedContentId} />
        )}
        <ImageWithDefault
          id={`${idPrefix}Artwork`}
          containerClassName={classNames(css.artworkImageContainer, {
            [css.smallerImage]: canShowUpsell,
          })}
          className={css.artworkImage}
          src={metadata.image}
          alt={metadata.title}
        />
        {renderDefaultSubtitle()}
        {isDiscord && !isMobile && (
          <div className={css.bottomSection}>
            {isLive && (
              <div className={css.liveBug}>
                <span>{getLocalizedText(LIVE).toUpperCase()}</span>
              </div>
            )}
          </div>
        )}
        {(!isDiscord || isMobile) && (
          <>
            <div className={css.scrubberContainer}>
              <Scrubber isInNowPlayingDialog />
            </div>
            {shouldShowNewControls && (
              <div className={css.controlPillContainer}>
                <div
                  className={classNames(css.controlPill, {
                    [css.speedControlPill]: canScrub,
                  })}
                >
                  {canScrub ? (
                    <SpeedControl iconContainerClass={css.speedControlIcon} />
                  ) : (
                    <span>{getLocalizedText(LIVE).toUpperCase()}</span>
                  )}
                </div>
              </div>
            )}
            <div
              className={classNames(css.controlsContainer, {
                [css.newControls]: shouldShowNewControls,
              })}
            >
              {shouldShowSpeedControl && (
                <SpeedControl iconContainerClass={css.speedControlContainer} />
              )}
              {shouldShowNewControls && canScrub && (
                <RewindFifteenIcon
                  width="40px"
                  height="40px"
                  onClick={() => player.seekBackward(15)}
                />
              )}
              <PlayButton
                className={classNames(css.playButtonContainer, {
                  [css.withSpeedControl]: shouldShowSpeedControl,
                })}
                customHeight={playButtonSize}
                customWidth={playButtonSize}
                breakpoint={breakpoint}
                customFill={cssVars['--secondary-color-7']}
                progressColor="white"
                enableDiscordControlTooltip
              />
              {shouldShowNewControls && canScrub && (
                <FastForwardFifteenIcon
                  width="40px"
                  height="40px"
                  onClick={() => player.seekForward(15)}
                />
              )}
            </div>
          </>
        )}
      </>
    );
  };

  const renderDefaultTitle = () => {
    const { testId, titleText, titleTextCss } = processTitle();

    return (
      <div ref={titleOuterContainerRef} className={css.title}>
        <div className={css.leftGradient} />
        <div ref={titleInnerContainerRef} className={titleTextCss} data-testid={idPrefix + testId}>
          {titleText}
        </div>
        <div className={css.rightGradient} />
      </div>
    );
  };

  const renderDesktopTitle = () => {
    const { testId, titleText, titleTextCss } = processTitle();

    return (
      <div ref={titleOuterContainerRef} className={css.title}>
        <ClampText
          clamp={1}
          ref={titleInnerContainerRef}
          className={titleTextCss}
          data-testid={idPrefix + testId}
          truncationHTML="<span>...</span>"
        >
          {titleText}
        </ClampText>
      </div>
    );
  };

  const renderDefaultSubtitle = () => {
    const { testId, subtitleText, subtitleTextCss } = processSubtitle();
    const { titleText } = processTitle();

    return (
      <>
        <div
          className={css.subtitle}
          ref={subtitleOuterContainerRef}
          data-testid={`${idPrefix}SubtitleContainer`}
        >
          <div className={css.leftGradient} />
          <div
            className={subtitleTextCss}
            ref={subtitleInnerContainerRef}
            data-testid={idPrefix + testId}
          >
            {subtitleText}
          </div>
          <div className={css.rightGradient} />
        </div>
        {isDiscord && !isMobile && (
          <Link className={css.subtitleProfileLink} to={guideItemPathname}>
            <span>{titleText}</span>
            <ChevronRight width="10px" height="18px" />
          </Link>
        )}
      </>
    );
  };

  const renderDesktopSubtitle = () => {
    const { testId, subtitleText, subtitleTextCss } = processSubtitle();

    return (
      <div
        className={css.subtitle}
        ref={subtitleOuterContainerRef}
        data-testid={`${idPrefix}SubtitleContainer`}
      >
        <ClampText
          clamp={1}
          className={subtitleTextCss}
          ref={subtitleInnerContainerRef}
          data-testid={idPrefix + testId}
          truncationHTML="<span>...</span>"
        >
          {subtitleText}
        </ClampText>
      </div>
    );
  };

  return isDesktopNowPlayingEnabled ? renderDesktopNowPlaying() : renderDefaultNowPlaying();
}

export default connectWithAuth(NowPlaying);
